import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import { RevealOnScroll } from "../shared";
import { COMPARISON_ROWS } from "./comparisonContent";
import EcosystemManifestPanel from "./EcosystemManifestPanel";

const LayerPill = ({ row, isActive, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(row.id)}
    className={`flex-shrink-0 rounded-full border px-3 py-2 text-xs font-semibold whitespace-nowrap transition-colors ${
      isActive
        ? "border-[#00D2FF]/50 text-partner-accent bg-[#00D2FF]/15"
        : "border-partner text-partner-muted bg-[#080A0F]"
    }`}
    data-testid={`comparison-layer-${row.id}`}
  >
    {row.shortLabel}
  </button>
);

const MobileComparisonCard = ({ row, index }) => (
  <motion.div
    key={row.id}
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -4 }}
    transition={{ duration: 0.2 }}
    className="rounded-xl border border-[#2A3348] bg-[#11151F] px-5 py-5"
    data-testid={`comparison-mobile-row-${row.id}`}
  >
    <div className="flex items-center justify-between gap-3 mb-3">
      <p className="font-mono text-[10px] text-partner-accent font-semibold tracking-wider">
        Architecture layer
      </p>
      <span className="font-mono text-xs text-partner-accent/70 tabular-nums">
        {String(index + 1).padStart(2, "0")} / {String(COMPARISON_ROWS.length).padStart(2, "0")}
      </span>
    </div>

    <h3 className="text-base font-bold text-partner leading-snug mb-4">{row.node}</h3>

    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-[#2A3348] bg-[#080A0F] p-4">
        <p className="text-xs font-semibold text-partner-muted uppercase tracking-wider mb-2">
          Weekend toy
        </p>
        <p className="text-sm text-partner-muted font-body leading-relaxed">{row.amateur}</p>
      </div>

      <div className="flex justify-center">
        <div className="w-8 h-8 rounded-full border border-[#00D2FF]/35 bg-[#00D2FF]/10 flex items-center justify-center">
          <ArrowRight className="w-4 h-4 text-partner-accent rotate-90" />
        </div>
      </div>

      <div className="rounded-lg border border-[#00D2FF]/20 bg-[#00D2FF]/5 p-4">
        <p className="text-xs font-semibold text-partner-accent uppercase tracking-wider mb-2">
          FractoLabs AI Employee
        </p>
        <p className="text-sm text-partner font-body leading-relaxed">{row.fractolabs}</p>
      </div>
    </div>

    <p className="mt-4 pt-4 border-t border-[#2A3348] text-xs text-partner-muted font-body">
      <span className="font-semibold text-partner-accent/80">Built by:</span> {row.owner}
    </p>
  </motion.div>
);

const MobileComparisonView = () => {
  const [activeRowId, setActiveRowId] = useState(COMPARISON_ROWS[0].id);
  const activeIndex = COMPARISON_ROWS.findIndex((r) => r.id === activeRowId);
  const activeRow = COMPARISON_ROWS[activeIndex >= 0 ? activeIndex : 0];

  return (
    <div className="lg:hidden" data-testid="comparison-mobile">
      <div
        className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 mb-4"
        role="tablist"
        aria-label="Architecture layers"
      >
        {COMPARISON_ROWS.map((row) => (
          <LayerPill
            key={row.id}
            row={row}
            isActive={activeRowId === row.id}
            onSelect={setActiveRowId}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        <MobileComparisonCard row={activeRow} index={activeIndex} />
      </AnimatePresence>
    </div>
  );
};

const ComparisonTableOnly = () => (
  <>
    <div className="hidden lg:block overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-partner">
            <th className="text-left py-4 px-4 text-partner-muted font-mono text-xs uppercase tracking-wider w-[18%]">
              Architecture Layer
            </th>
            <th className="text-left py-4 px-4 text-partner-muted font-mono text-xs uppercase tracking-wider w-[27%]">
              Typical Chatbot (GPT / Make / Zapier)
            </th>
            <th className="text-left py-4 px-4 text-partner-accent font-mono text-xs uppercase tracking-wider w-[27%]">
              FractoLabs AI Employee
            </th>
            <th className="text-left py-4 px-4 text-partner-accent font-mono text-xs uppercase tracking-wider w-[18%]">
              FractoLabs Owner
            </th>
          </tr>
        </thead>
        <tbody>
          {COMPARISON_ROWS.map((row) => (
            <tr key={row.id} className="border-b border-partner hover:bg-[#11151F] transition-colors">
              <td className="py-4 px-4 font-semibold text-partner align-top">{row.node}</td>
              <td className="py-4 px-4 text-partner-muted font-body leading-relaxed align-top">{row.amateur}</td>
              <td className="py-4 px-4 text-partner font-body leading-relaxed align-top">{row.fractolabs}</td>
              <td className="py-4 px-4 text-partner-accent/80 font-mono text-xs align-top">{row.owner}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <RevealOnScroll delay={0.08}>
      <MobileComparisonView />
    </RevealOnScroll>
  </>
);

const ComparisonTableBody = () => (
  <>
    <ComparisonTableOnly />
    <div className="mt-12">
      <EcosystemManifestPanel />
    </div>
  </>
);

const ComparisonTableSection = ({ id, compact = false }) => (
  <section
    id={id}
    className="section-padding section-alt relative overflow-hidden"
    data-testid="comparison-table"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <RevealOnScroll className="mb-12">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 leading-tight">
          Industrial Infrastructure vs. <span className="gradient-text">Weekend Toys</span>
        </h2>
        <p className="text-partner-muted font-body max-w-3xl leading-relaxed">
          Most agencies try to build "AI bots" using basic prompt engineering on platforms like Make, Zapier, or Custom GPTs. Below is the architectural breakdown of why amateur scripts create massive operational risks for your clients, and how FractoLabs delivers enterprise-grade stability.
        </p>
      </RevealOnScroll>

      {compact ? (
        <>
          <RevealOnScroll delay={0.06}>
            <div className="mb-8">
              <EcosystemManifestPanel />
            </div>
          </RevealOnScroll>
          <details className="group rounded-xl border border-partner bg-[#11151F]" data-testid="infrastructure-compact-details">
            <summary className="cursor-pointer list-none px-5 py-4 text-sm font-semibold text-partner flex items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
              Full architecture breakdown
              <ChevronRight className="w-4 h-4 text-partner-muted transition-transform group-open:rotate-90 shrink-0" />
            </summary>
            <div className="px-5 pb-8 pt-2 border-t border-partner">
              <ComparisonTableOnly />
            </div>
          </details>
        </>
      ) : (
        <ComparisonTableBody />
      )}
    </div>
  </section>
);

export default ComparisonTableSection;
